export type OutcomeSetProbabilityNextTask = [number, number];
export type OutcomeSetSizeProbability = [number, number];
export type NextTaskSelectionWeight = Partial<Record<number, number>>;
export type TaskDurationVariability = 'low' | 'moderate' | 'high';

export interface SimulationNodeBlock {
  task_order: number;
  task_name: string;
  task_UUID: string;
  task_duration_avg: number;
  task_duration_stdev: number | null;
  task_duration_variability: TaskDurationVariability;
  workers: number | null;
  resource_id: string | null;
  resource_units: number | null;
  queue_priority: number | null;
  task_overrides_on_exit: TaskOverridesOnExit[] | {};
  next_task_selection_weight: NextTaskSelectionWeight | {};
  allow_abandon: boolean;
  max_wait_time: number;
  previous_task: number[];
  previous_task_dependency_type: string;
  next_task_type: string;
  next_task: number[] | OutcomeSetProbabilityNextTask[];
  is_terminal: boolean;
  interruptions: SimulationResourceInterruption[];
  cust_alias?: string;
  group?: string;
  group_assignation?: string;
}

export interface TaskOverridesOnExit {
  id?: number;
  queue_priority?: number;
}

export interface SimulationArrivalScheduleShift {
  start_time: string;
  end_time: string;
}

export interface SimulationArrivalSchedule {
  workdays: string[];
  shifts: SimulationArrivalScheduleShift[];
  holidays: string[];
}

export interface SimulationSendValues {
  iterations: number;
  simulation_period_minutes: number;
  resources_structure: SimulationResourceStructure[];
  tasks_structure: SimulationNodeBlock[];
  arrival_rate_dic: Record<string, number>;
  simulation_start_datetime: string;
  simulation_timezone: string;
  arrival_schedule: SimulationArrivalSchedule;
  show_detail_needed: boolean;
  seed: number;
}

export interface SimulationSavedConfig {
  model_UUID?: string;
  simulation_UUID: string;
  simulation_name: string;
  simulation_structure: string | SimulationSendValues;
}

export interface SimulationTaskReference {
  task_order: number;
  task_name: string;
  task_uuid: string;
}

export interface SimulationResourceReference {
  resource_id: string;
  resource_name: string;
}

export interface SimulationGlobalAverage
  extends Omit<
      SimulationNodeBlock,
      | 'task_UUID'
      | 'workers'
      | 'task_duration_stdev'
      | 'previous_task'
      | 'next_task'
      | 'is_terminal'
      | 'cust_alias'
      | 'task_overrides_on_exit'
    >,
    SimulationTaskReference {
  workers: number | null;
  resource_id: string | null;
  resource_units: number;
  resource_continuity_group: string | null;
  resource_continuity_mode: string | null;
  queue_priority: number;
  task_overrides_on_exit: Record<string, unknown>;
  task_duration_stdev: number | null;
  avg_arrivals: number;
  avg_served: number;
  avg_abandoned: number;
  avg_abandon_rate: number;
  avg_abandon_wait: number;
  avg_waiting_time: number;
  avg_service_time: number;
  avg_utilization: number;
  avg_wip: number;
  avg_queue: number;
  avg_in_service: number;
  avg_max_wip: number;
  avg_max_queue: number;
  avg_max_in_service: number;
  avg_throughput: number;
  avg_minutes_per_customer: number;
  avg_theoretical_capacity: number;
  avg_last_finish_time: number;
}

export interface SimulationResourceGlobalAverage
  extends SimulationResourceReference {
  capacity: number;
  items: string[];
  avg_requests: number;
  avg_served: number;
  avg_waiting_time: number;
  avg_service_time: number;
  avg_utilization: number;
  avg_queue: number;
  avg_in_use: number;
  avg_max_queue: number;
  avg_max_in_use: number;
  avg_throughput: number;
  avg_minutes_per_request: number;
  avg_theoretical_capacity: number;
  avg_last_finish_time: number;
}

export interface SimulationResourceItemGlobalAverage
  extends SimulationResourceReference {
  item_name: string;
  avg_busy_time: number;
  avg_utilization: number;
  avg_assignments: number;
}

export interface SimulationBottleneck extends SimulationTaskReference {
  task_duration_avg: number;
  task_workers: number | null;
  resource_id: string | null;
  resource_units: number;
  resource_continuity_group: string | null;
  resource_continuity_mode: string | null;
  avg_utilization: number;
  avg_queue: number;
  avg_wip: number;
  avg_waiting: number;
  avg_throughput: number;
  avg_abandon_rate: number;
  rank: number;
}

export interface SimulationResourceBottleneck
  extends SimulationResourceReference {
  capacity: number;
  avg_utilization: number;
  avg_queue: number;
  avg_waiting: number;
  avg_throughput: number;
  rank: number;
}

export interface SimulationCycleTime {
  customers_finished: number;
  avg_cycle_time: number;
  min_cycle_time: number;
  max_cycle_time: number;
  p50: number;
  p90: number;
  p95: number;
}

export interface SimulationGraphAxis {
  label: string;
  values?: Array<string | number>;
}

export interface SimulationGraphPoint {
  task_order?: number;
  task_uuid?: string;
  label?: string;
  x: number;
  y: number;
}

export interface SimulationGraphSeries {
  name: string;
  values?: number[];
  points?: SimulationGraphPoint[];
}

export interface SimulationGraph {
  graph_id: number;
  title: string;
  subtitle: string;
  chart_type: string;
  x_axis: SimulationGraphAxis;
  y_axis: SimulationGraphAxis;
  legend: string[];
  series: SimulationGraphSeries[];
  statistics?: SimulationCycleTime;
}

export interface SimulationEntryGeneratorConfig {
  iterations: number;
  simulation_period_minutes: number;
}

export interface SimulationEntryGeneratorDefinition {
  initial_task_order: number;
  arrival_rate: number;
  generator_name: string;
}

export type SimulationEntryGenerator =
  | SimulationEntryGeneratorConfig
  | SimulationEntryGeneratorDefinition;

export interface SimulationOutputFile {
  name: string;
  path: string | null;
}

export interface FullSimulationReport {
  simulation_report: string;
  simulation_result: SimulationReturnValues;
  other_files: SimulationOutputFile[];
}

export interface SimulationReturnValues {
  GLOBAL_AVERAGES: SimulationGlobalAverage[];
  RESOURCE_GLOBAL_AVERAGES: SimulationResourceGlobalAverage[];
  RESOURCE_ITEM_GLOBAL_AVERAGES: SimulationResourceItemGlobalAverage[];
  BOTTLENECK: SimulationBottleneck[];
  PRIMARY_BOTTLENECK: SimulationTaskReference;
  RESOURCE_BOTTLENECK: SimulationResourceBottleneck[];
  PRIMARY_RESOURCE_BOTTLENECK: SimulationResourceReference;
  CYCLE_TIME: SimulationCycleTime;
  GRAPHS: SimulationGraph[];
  ENTRY_GENERATORS: SimulationEntryGenerator[];
  DETAIL_CSV_FILE: string | null;
  RESULT_JSON_FILE: string;
}

export interface SimulationResourceInterruption {
  type: string;
  every_minutes: number;
  duration_minutes: number;
  reason: string;
}

export interface SimulationResourceStructureItem {
  item_name: string;
  interruptions?: SimulationResourceInterruption[];
}

export interface SimulationResourceStructure {
  resource_id: string;
  resource_name: string;
  capacity: number;
  interruptions?: SimulationResourceInterruption[];
  items: SimulationResourceStructureItem[] | string[];
}
